import { useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { useSearchParams } from "react-router"

import {
  useADWCampaigns,
  useAdsManagerCampaigns,
  useAdsManagerUsers,
  useKLOWCampaigns,
  usePauseCampaign,
  useResumeCampaign,
  useUnassignedCampaigns,
  type CampaignQueryParams,
} from "@/api/hooks/adsManager"
import { buildAccountFilterGroups } from "./lib/accountFilterOptions"
import { buildUserFilterGroups } from "./lib/userFilterOptions"
import { useTikTokAccounts } from "@/api/hooks/tiktok"
import { useAuth } from "@/auth/AuthProvider"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { NavbarTabList, navbarTabClass } from "@/components/NavbarTabs"
import { useNavbarLeftAction, usePageTitle } from "@/layouts/pageChrome"

import {
  ADW_COLUMN_STORAGE_KEY,
  ADW_VISIBILITY_STORAGE_KEY,
  KLOW_COLUMN_STORAGE_KEY,
  KLOW_VISIBILITY_STORAGE_KEY,
  ROOTS_COLUMN_STORAGE_KEY,
  ROOTS_VISIBILITY_STORAGE_KEY,
  UNASSIGNED_COLUMN_STORAGE_KEY,
  UNASSIGNED_VISIBILITY_STORAGE_KEY,
  columnsForTab,
  saveColumnOrder,
  saveColumnVisibility,
} from "./lib/columns"
import {
  adsManagerReducer,
  createInitialState,
  type AdsManagerTab,
} from "./state/adsManagerReducer"
import { getCampaignAccountName } from "./lib/accountName"
import { CampaignTable } from "./widgets/CampaignTable"
import {
  computePresetRangeForTab,
  reportingTimezoneForTab,
  type AdsManagerDatePreset,
} from "./lib/datePresets"
import { countActiveFilters } from "./lib/filters"
import {
  formatMetricFilterChip,
  hasMetricFilterBounds,
  isFilterableMetricColumn,
  serializeMetricFilters,
} from "./lib/metricFilters"
import { DateRangeSelector } from "./widgets/DateRangeSelector"
import { FilterBar } from "./widgets/FilterBar"
import { FiltersMobileDialog } from "./widgets/FiltersMobileDialog"
import { CampaignDetailModal } from "./widgets/CampaignDetailModal"
import { KeywordsModal } from "./widgets/KeywordsModal"
import { statusFilterChipLabel } from "./widgets/ColumnStatusFilter"
import { Pagination } from "@/components/Pagination"

const TABS: { value: AdsManagerTab; label: string; networkSlug?: string; adminOnly?: boolean }[] = [
  { value: "roots", label: "Roots", networkSlug: "roots" },
  { value: "klow", label: "KLOW", networkSlug: "klow" },
  { value: "adw", label: "ADW", networkSlug: "adw" },
  { value: "brm", label: "BRM", adminOnly: true },
  { value: "unassigned", label: "Unassigned", adminOnly: true },
]

export default function AdsManagerPage() {
  usePageTitle("", "Ads Manager")
  const { isAdmin, hasNetwork, user } = useAuth()
  const [searchParams] = useSearchParams()
  const hasUrlUserId = Boolean(searchParams.get("user_id"))

  const urlUserId = searchParams.get("user_id") ?? undefined
  const urlAccountId = searchParams.get("account_id") ?? undefined

  const [state, dispatch] = useReducer(
    adsManagerReducer,
    {
      urlTab: searchParams.get("network") ?? undefined,
      urlUserId: searchParams.get("user_id") ?? undefined,
      urlAccountId: searchParams.get("account_id") ?? undefined,
      urlStartDate: searchParams.get("start_date") ?? undefined,
      urlEndDate: searchParams.get("end_date") ?? undefined,
      currentUserId: user?.id,
      currentUserRole: user?.role,
      networkSlugs: user?.network_slugs,
    },
    createInitialState,
  )

  useEffect(() => {
    if (hasUrlUserId) return
    if (user?.role !== "team_leader" || !user.id) return
    if (state.selectedUserIds.length > 0) return
    dispatch({ type: "SET_USER_IDS", ids: [user.id] })
  }, [hasUrlUserId, user?.id, user?.role, state.selectedUserIds.length])

  // Unknown status is admin-only — reset stale selection for other roles.
  useEffect(() => {
    if (isAdmin) return
    if (state.status !== "UNKNOWN") return
    dispatch({ type: "SET_STATUS", value: "all" })
  }, [isAdmin, state.status])

  // Rolling presets (Today, Yesterday, …) must track the tab's reporting calendar.
  // Initial state used to always use browser-local midnight, so at 00:05 Kyiv the
  // Roots tab queried "tomorrow" in NY while the Today button fixed it on click.
  useEffect(() => {
    if (state.datePreset === "custom") return

    const syncRollingPreset = () => {
      const range = computePresetRangeForTab(state.datePreset, state.activeTab)
      if (
        range.startDate === state.startDate &&
        range.endDate === state.endDate
      ) {
        return
      }
      dispatch({
        type: "SET_PRESET",
        preset: state.datePreset,
        startDate: range.startDate,
        endDate: range.endDate,
        displayLabel: range.displayLabel,
      })
    }

    syncRollingPreset()

    const onVisibility = () => {
      if (document.visibilityState === "visible") syncRollingPreset()
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [state.datePreset, state.activeTab, state.startDate, state.endDate])

  const [filtersOpen, setFiltersOpen] = useState(false)

  const activeFilterCount = countActiveFilters(
    state.status,
    state.selectedUserIds,
    state.selectedAccountIds,
  )

  // Keywords modal state
  const [keywordsModal, setKeywordsModal] = useState<{
    open: boolean
    campaignId: string
    campaignName: string
  }>({ open: false, campaignId: "", campaignName: "" })

  const [detailModal, setDetailModal] = useState<{
    open: boolean
    campaignId: string
    campaignName: string
    advertiserId: string
  }>({ open: false, campaignId: "", campaignName: "", advertiserId: "" })

  // Drop legacy Budget/Bid metric filters (no longer filterable).
  useEffect(() => {
    for (const columnId of ["budget", "bid"] as const) {
      if (hasMetricFilterBounds(state.metricFilters[columnId])) {
        dispatch({ type: "SET_METRIC_FILTER", columnId, bounds: null })
      }
    }
  }, [state.metricFilters])

  // Build query params
  const metricFiltersParam = useMemo(
    () => serializeMetricFilters(state.metricFilters),
    [state.metricFilters],
  )

  // Keep the input snappy; only hit the API after typing pauses (~300ms).
  const [debouncedSearch, setDebouncedSearch] = useState(state.search)
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(state.search), 300)
    return () => window.clearTimeout(t)
  }, [state.search])

  const queryParams = useMemo<CampaignQueryParams>(
    () => ({
      page: state.page,
      page_size: state.pageSize,
      status: state.status,
      search: debouncedSearch,
      start_date: state.startDate,
      end_date: state.endDate,
      sort_by: state.sortBy,
      sort_order: state.sortOrder,
      advertiser_ids: state.selectedAccountIds.length
        ? state.selectedAccountIds
        : undefined,
      user_ids: state.selectedUserIds.length
        ? state.selectedUserIds
        : undefined,
      metric_filters: metricFiltersParam,
    }),
    [
      state.page,
      state.pageSize,
      state.status,
      debouncedSearch,
      state.startDate,
      state.endDate,
      state.sortBy,
      state.sortOrder,
      state.selectedAccountIds,
      state.selectedUserIds,
      metricFiltersParam,
    ],
  )

  // Queries
  const rootsQuery = useAdsManagerCampaigns(queryParams, {
    enabled: state.activeTab === "roots",
  })
  const adwQuery = useADWCampaigns(queryParams, {
    enabled: state.activeTab === "adw",
  })
  const klowQuery = useKLOWCampaigns(queryParams, {
    enabled: state.activeTab === "klow" && hasNetwork("klow"),
  })
  const unassignedQuery = useUnassignedCampaigns(queryParams, {
    enabled: state.activeTab === "unassigned" && isAdmin,
  })
  const usersQuery = useAdsManagerUsers({
    startDate: state.startDate,
    endDate: state.endDate,
    alwaysIncludeUserId: urlUserId ? Number(urlUserId) : undefined,
  })
  const tiktokAccountsQuery = useTikTokAccounts({
    includeDeleted: true,
    activeInRange: true,
    startDate: state.startDate,
    endDate: state.endDate,
    alwaysIncludeAdvertiserId: urlAccountId,
  })

  // Mutations
  const pauseMutation = usePauseCampaign()
  const resumeMutation = useResumeCampaign()

  // Active query
  const activeQuery = useMemo(() => {
    switch (state.activeTab) {
      case "roots":
        return rootsQuery
      case "adw":
        return adwQuery
      case "klow":
        return klowQuery
      case "unassigned":
        return unassignedQuery
      default:
        return rootsQuery
    }
  }, [state.activeTab, rootsQuery, adwQuery, klowQuery, unassignedQuery])

  const rows = useMemo(() => activeQuery.data?.data ?? [], [activeQuery.data])
  const totals = activeQuery.data?.totals ?? null
  const pagination = activeQuery.data?.pagination

  // Error message
  const errorMessage = useMemo(() => {
    if (activeQuery.isError) {
      const err = activeQuery.error
      return err instanceof Error
        ? err.message
        : "Failed to load campaigns"
    }
    if (activeQuery.data && !activeQuery.data.success) {
      return "Failed to load campaigns"
    }
    return null
  }, [activeQuery.isError, activeQuery.error, activeQuery.data])

  // Accounts for filter — active first, deleted grouped at bottom with suffix.
  const accountGroups = useMemo(() => {
    const connected = tiktokAccountsQuery.data?.accounts ?? []
    if (connected.length > 0) {
      return buildAccountFilterGroups(connected)
    }

    const seen = new Map<string, string>()
    for (const row of rows) {
      if (!seen.has(row.advertiser_id)) {
        seen.set(row.advertiser_id, getCampaignAccountName(row))
      }
    }
    const fallback = Array.from(seen, ([id, name]) => ({
      advertiser_id: id,
      advertiser_name: name,
      balance: null,
      currency: null,
    }))
    return buildAccountFilterGroups(fallback)
  }, [rows, tiktokAccountsQuery.data?.accounts])

  const userGroups = useMemo(
    () => buildUserFilterGroups(usersQuery.data ?? []),
    [usersQuery.data],
  )

  // Column state for current tab
  const currentColumns = columnsForTab(state.activeTab)
  const currentColumnOrder = useMemo(() => {
    switch (state.activeTab) {
      case "roots":
        return state.rootsColumnOrder
      case "adw":
        return state.adwColumnOrder
      case "klow":
        return state.klowColumnOrder
      case "unassigned":
        return state.unassignedColumnOrder
      default:
        return state.rootsColumnOrder
    }
  }, [
    state.activeTab,
    state.rootsColumnOrder,
    state.adwColumnOrder,
    state.klowColumnOrder,
    state.unassignedColumnOrder,
  ])

  const currentHiddenColumns = useMemo(() => {
    switch (state.activeTab) {
      case "roots":
        return state.rootsHiddenColumns
      case "adw":
        return state.adwHiddenColumns
      case "klow":
        return state.klowHiddenColumns
      case "unassigned":
        return state.unassignedHiddenColumns
      default:
        return state.rootsHiddenColumns
    }
  }, [
    state.activeTab,
    state.rootsHiddenColumns,
    state.adwHiddenColumns,
    state.klowHiddenColumns,
    state.unassignedHiddenColumns,
  ])

  const activeMetricFilterChips = useMemo(() => {
    const colMap = new Map(currentColumns.map((c) => [c.id, c.label]))
    return Object.entries(state.metricFilters)
      .filter(
        ([columnId, bounds]) =>
          hasMetricFilterBounds(bounds) &&
          isFilterableMetricColumn(columnId, "number"),
      )
      .map(([columnId, bounds]) => ({
        columnId,
        label: formatMetricFilterChip(
          columnId,
          colMap.get(columnId) ?? columnId,
          bounds,
        ),
      }))
  }, [state.metricFilters, currentColumns])

  // Handlers
  const handleColumnOrderChange = useCallback(
    (order: string[]) => {
      dispatch({ type: "SET_COLUMN_ORDER", tab: state.activeTab, order })
      const storageKey =
        state.activeTab === "adw"
          ? ADW_COLUMN_STORAGE_KEY
          : state.activeTab === "klow"
            ? KLOW_COLUMN_STORAGE_KEY
            : state.activeTab === "unassigned"
              ? UNASSIGNED_COLUMN_STORAGE_KEY
              : ROOTS_COLUMN_STORAGE_KEY
      saveColumnOrder(storageKey, order)
    },
    [state.activeTab],
  )

  const handleToggleColumn = useCallback(
    (columnId: string) => {
      dispatch({
        type: "TOGGLE_COLUMN_VISIBILITY",
        tab: state.activeTab,
        columnId,
      })
      // Persist after state update (compute new set)
      const current = currentHiddenColumns
      const next = new Set(current)
      if (next.has(columnId)) next.delete(columnId)
      else next.add(columnId)
      const storageKey =
        state.activeTab === "adw"
          ? ADW_VISIBILITY_STORAGE_KEY
          : state.activeTab === "klow"
            ? KLOW_VISIBILITY_STORAGE_KEY
            : state.activeTab === "unassigned"
              ? UNASSIGNED_VISIBILITY_STORAGE_KEY
              : ROOTS_VISIBILITY_STORAGE_KEY
      saveColumnVisibility(storageKey, next)
    },
    [state.activeTab, currentHiddenColumns],
  )

  const campaignToggleBusyId =
    pauseMutation.isPending || resumeMutation.isPending
      ? (pauseMutation.variables ?? resumeMutation.variables ?? null)
      : null

  const handlePauseResume = useCallback(
    (campaignId: string, currentStatus: string) => {
      // Only block re-clicks on the campaign already in flight.
      if (campaignToggleBusyId === campaignId) return
      if (currentStatus === "ENABLE" || currentStatus === "ACTIVE") {
        pauseMutation.mutate(campaignId)
      } else {
        resumeMutation.mutate(campaignId)
      }
    },
    [campaignToggleBusyId, pauseMutation, resumeMutation],
  )

  const handleKeywordsClick = useCallback(
    (campaignId: string, campaignName: string) => {
      setKeywordsModal({ open: true, campaignId, campaignName })
    },
    [],
  )

  const handleDetailClick = useCallback(
    (campaignId: string, campaignName: string, advertiserId: string) => {
      setDetailModal({ open: true, campaignId, campaignName, advertiserId })
    },
    [],
  )

  const handlePresetChange = useCallback(
    (
      preset: AdsManagerDatePreset,
      startDate: string,
      endDate: string,
      displayLabel: string,
    ) => {
      dispatch({ type: "SET_PRESET", preset, startDate, endDate, displayLabel })
    },
    [],
  )

  const handleCustomRange = useCallback(
    (startDate: string, endDate: string, displayLabel: string) => {
      dispatch({
        type: "SET_CUSTOM_RANGE",
        startDate,
        endDate,
        displayLabel,
      })
    },
    [],
  )

  const isLoading =
    activeQuery.isFetching && !activeQuery.data

  const visibleTabs = useMemo(
    () =>
      TABS.filter((tab) => {
        if (tab.adminOnly) return isAdmin
        if (tab.networkSlug) return hasNetwork(tab.networkSlug)
        return true
      }),
    [hasNetwork, isAdmin],
  )

  useEffect(() => {
    if (visibleTabs.some((tab) => tab.value === state.activeTab)) return
    const fallback = visibleTabs[0]?.value
    if (fallback) dispatch({ type: "SET_TAB", tab: fallback })
  }, [visibleTabs, state.activeTab])

  const navbarTabs = useMemo(
    () => (
      <NavbarTabList>
        {visibleTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => dispatch({ type: "SET_TAB", tab: tab.value })}
            className={navbarTabClass(state.activeTab === tab.value)}
            data-tab={tab.value}
          >
            {tab.label}
          </button>
        ))}
      </NavbarTabList>
    ),
    [visibleTabs, state.activeTab],
  )
  useNavbarLeftAction(navbarTabs)

  const renderSearchInput = (className: string) => (
    <input
      type="text"
      placeholder="Search campaigns..."
      value={state.search}
      onChange={(e) =>
        dispatch({ type: "SET_SEARCH", value: e.target.value })
      }
      className={className}
      aria-label="Search campaigns"
    />
  )

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#3a3a3a]">
      {/* Fixed-screen body: only the table region scrolls */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1.5 pt-1.5 sm:px-2 sm:pt-2">
        {errorMessage && (
          <Alert
            variant="destructive"
            className="mb-2 shrink-0 border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {state.activeTab === "brm" ? (
          <div className="flex flex-1 items-center justify-center py-16 text-white">
            BRM integration coming soon
          </div>
        ) : (
          <>
            <div className="mb-1.5 shrink-0 rounded-md border border-[#3f3f3f] bg-[#252525] px-2 py-1.5">
              <div className="flex w-full min-w-0 flex-col gap-1.5 md:flex-row md:items-center md:gap-2">
                <div className="hidden min-w-0 flex-1 items-center gap-1.5 overflow-x-auto overscroll-x-contain py-0.5 [-webkit-overflow-scrolling:touch] md:flex">
                  <FilterBar
                    userGroups={userGroups}
                    selectedUserIds={state.selectedUserIds}
                    onUserIdsChange={(ids) =>
                      dispatch({ type: "SET_USER_IDS", ids })
                    }
                    selectedAccountIds={state.selectedAccountIds}
                    onAccountIdsChange={(ids) =>
                      dispatch({ type: "SET_ACCOUNT_IDS", ids })
                    }
                    accountGroups={accountGroups}
                    columns={currentColumns}
                    hiddenColumns={currentHiddenColumns}
                    onToggleColumn={handleToggleColumn}
                  />
                  {renderSearchInput(
                    "h-7 min-w-[140px] flex-1 rounded border border-[#4a4a4a] bg-[#2d2d2d] px-2.5 text-xs text-white placeholder-white/50 focus:border-[#8b9aff] focus:outline-none md:max-w-[260px]",
                  )}
                </div>

                <div className="flex min-w-0 items-center gap-2 md:hidden">
                  <button
                    type="button"
                    onClick={() => setFiltersOpen(true)}
                    className="relative flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-[#555] bg-[#2d2d2d] px-3 text-xs font-medium text-white shadow-sm hover:bg-[#3a3a3a]"
                  >
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#7488e8] px-1 text-[10px] font-bold text-white">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => activeQuery.refetch()}
                    title="Refresh data"
                    aria-label="Refresh data"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#676d78] bg-[#4f5561] p-0 text-xs text-white shadow-sm transition-colors hover:bg-[#5a616f]"
                  >
                    <span aria-hidden>{"\uD83D\uDD04"}</span>
                  </button>
                </div>

                <div className="flex min-w-0 flex-1 items-center gap-1.5 md:ml-auto md:flex-none md:justify-end">
                  <button
                    type="button"
                    onClick={() => activeQuery.refetch()}
                    title="Refresh data"
                    aria-label="Refresh data"
                    className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[#676d78] bg-[#4f5561] p-0 text-xs text-white shadow-sm transition-colors hover:bg-[#5a616f] md:flex"
                  >
                    <span aria-hidden>{"\uD83D\uDD04"}</span>
                  </button>
                  <DateRangeSelector
                    datePreset={state.datePreset}
                    startDate={state.startDate}
                    endDate={state.endDate}
                    displayLabel={state.dateDisplayLabel}
                    onPresetChange={handlePresetChange}
                    onCustomRangeApply={handleCustomRange}
                    useReportingTimezone={Boolean(
                      reportingTimezoneForTab(state.activeTab),
                    )}
                    reportingTimeZone={
                      reportingTimezoneForTab(state.activeTab) ?? undefined
                    }
                  />
                </div>
              </div>
            </div>

            <FiltersMobileDialog
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              userGroups={userGroups}
              selectedUserIds={state.selectedUserIds}
              onUserIdsChange={(ids) =>
                dispatch({ type: "SET_USER_IDS", ids })
              }
              selectedAccountIds={state.selectedAccountIds}
              onAccountIdsChange={(ids) =>
                dispatch({ type: "SET_ACCOUNT_IDS", ids })
              }
              accountGroups={accountGroups}
              columns={currentColumns}
              hiddenColumns={currentHiddenColumns}
              onToggleColumn={handleToggleColumn}
            />

            {/* Mobile search — desktop search lives in the toolbar after Columns */}
            <div className="mb-1.5 shrink-0 md:hidden">
              {renderSearchInput(
                "h-9 w-full rounded border border-[#4a4a4a] bg-[#2d2d2d] px-3 text-sm text-white placeholder-white/50 focus:border-[#8b9aff] focus:outline-none",
              )}
            </div>

            {(statusFilterChipLabel(state.status) ||
              activeMetricFilterChips.length > 0) && (
              <div className="mb-1.5 flex shrink-0 flex-wrap items-center gap-1.5">
                {statusFilterChipLabel(state.status) && (
                  <button
                    type="button"
                    onClick={() =>
                      dispatch({ type: "SET_STATUS", value: "all" })
                    }
                    className="inline-flex items-center gap-1 rounded-full border border-[#7488e8]/40 bg-[#7488e8]/15 px-2.5 py-0.5 text-[11px] font-medium text-[#c5cef7] hover:bg-[#7488e8]/25"
                    title="Clear status filter"
                  >
                    {statusFilterChipLabel(state.status)}
                    <span aria-hidden className="text-[#7488e8]">
                      ×
                    </span>
                  </button>
                )}
                {activeMetricFilterChips.map((chip) => (
                  <button
                    key={chip.columnId}
                    type="button"
                    onClick={() =>
                      dispatch({
                        type: "SET_METRIC_FILTER",
                        columnId: chip.columnId,
                        bounds: null,
                      })
                    }
                    className="inline-flex items-center gap-1 rounded-full border border-[#7488e8]/40 bg-[#7488e8]/15 px-2.5 py-0.5 text-[11px] font-medium text-[#c5cef7] hover:bg-[#7488e8]/25"
                    title={`Clear ${chip.label}`}
                  >
                    {chip.label}
                    <span aria-hidden className="text-[#7488e8]">
                      ×
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    dispatch({ type: "CLEAR_METRIC_FILTERS" })
                    if (state.status !== "all") {
                      dispatch({ type: "SET_STATUS", value: "all" })
                    }
                  }}
                  className="px-1.5 text-[11px] text-white/55 hover:text-white"
                >
                  Clear filters
                </button>
              </div>
            )}

            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <CampaignTable
                rows={rows}
                totals={totals}
                columns={currentColumns}
                columnOrder={currentColumnOrder}
                hiddenColumns={currentHiddenColumns}
                sortBy={state.sortBy}
                sortOrder={state.sortOrder}
                onSort={(key, columnType) =>
                  dispatch({ type: "SET_SORT", sortBy: key, columnType })
                }
                onColumnOrderChange={handleColumnOrderChange}
                onPauseResume={handlePauseResume}
                pauseResumeBusyId={campaignToggleBusyId}
                onKeywordsClick={handleKeywordsClick}
                onDetailClick={handleDetailClick}
                metricFilters={state.metricFilters}
                onMetricFilterChange={(columnId, bounds) =>
                  dispatch({ type: "SET_METRIC_FILTER", columnId, bounds })
                }
                statusFilter={state.status}
                onStatusFilterChange={(value) =>
                  dispatch({ type: "SET_STATUS", value })
                }
                showUnknownStatus={isAdmin}
                isLoading={isLoading}
                showToggle={true}
                startDate={state.startDate}
                endDate={state.endDate}
              />
            </div>
          </>
        )}
      </div>

      {/* Viewport-fixed footer: Showing left, pages right */}
      {state.activeTab !== "brm" && pagination && (
        <Pagination
          className="border-t border-[#4a4a4a] bg-[#303030] px-2 py-2 sm:px-3"
          page={pagination.page}
          totalPages={pagination.pages}
          total={pagination.total}
          pageSize={state.pageSize}
          onPageChange={(p) => dispatch({ type: "SET_PAGE", page: p })}
          onPageSizeChange={(ps) =>
            dispatch({ type: "SET_PAGE_SIZE", pageSize: ps })
          }
        />
      )}

      <KeywordsModal
        open={keywordsModal.open}
        onOpenChange={(open) =>
          setKeywordsModal((prev) => ({ ...prev, open }))
        }
        campaignId={keywordsModal.campaignId}
        campaignName={keywordsModal.campaignName}
        startDate={state.startDate}
        endDate={state.endDate}
        selectedUserIds={state.selectedUserIds}
      />

      <CampaignDetailModal
        open={detailModal.open}
        onOpenChange={(open) =>
          setDetailModal((prev) => ({ ...prev, open }))
        }
        campaignId={detailModal.campaignId}
        campaignName={detailModal.campaignName}
        advertiserId={detailModal.advertiserId}
        startDate={state.startDate}
        endDate={state.endDate}
        useReportingTimezone={state.activeTab === "roots" || state.activeTab === "klow"}
      />
    </div>
  )
}
